import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { authService } from '@/services/authService';
import { usePermissions } from '@/composables/usePermissions';
import type { AccountInfo } from '@azure/msal-browser';

export const useAuthStore = defineStore('auth', () => {
  // Use the shared permissions composable
  const permissionsComposable = usePermissions();
  let initializationPromise: Promise<void> | null = null;

  // State
  const account = ref<AccountInfo | null>(null);
  const isInitialized = ref(false);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const permissionsVerified = ref(false);

  // Getters
  // User is only fully authenticated if they have an MSAL account AND backend permissions are verified
  const isAuthenticated = computed(() => 
    account.value !== null && permissionsVerified.value
  );
  
  const userInfo = computed(() => {
    if (!account.value) return null;
    return {
      name: account.value.name,
      email: account.value.username,
      username: account.value.username,
      tenantId: account.value.tenantId,
      localAccountId: account.value.localAccountId,
    };
  });

  // Permission getters - delegate to permissions composable
  const userRoles = computed(() => permissionsComposable.roles.value);
  const userScopes = computed(() => permissionsComposable.scopes.value);
  const hasPermissions = computed(() => permissionsComposable.isLoaded.value);

  function resetAuthenticatedState() {
    account.value = null;
    permissionsVerified.value = false;
    permissionsComposable.resetPermissions();
  }

  // Actions
  async function initialize() {
    if (isInitialized.value) return;

    if (initializationPromise) {
      await initializationPromise;
      return;
    }

    initializationPromise = (async () => {
      try {
        isLoading.value = true;
        error.value = null;
        await authService.initialize();
        account.value = authService.getAccount();
        isInitialized.value = true;

        if (account.value) {
          await loadPermissions();
        }
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to initialize auth';
        console.error('Auth initialization error:', err);
      } finally {
        isLoading.value = false;
        initializationPromise = null;
      }
    })();

    await initializationPromise;
  }

  /**
   * Load user permissions from the backend
   * This verifies that the user has proper access in the backend system
   */
  async function loadPermissions() {
    if (!account.value) {
      console.warn('Cannot load permissions: user not authenticated');
      resetAuthenticatedState();
      return;
    }

    try {
      await permissionsComposable.loadPermissions();
      console.log('User permissions loaded:', permissionsComposable.permissionsSummary.value);
      
      // Check if user has any roles or scopes
      const hasRoles = permissionsComposable.roles.value.length > 0;
      const hasScopes = permissionsComposable.scopes.value.length > 0;
      
      if (!hasRoles && !hasScopes) {
        console.warn('User authenticated successfully but has no roles or scopes assigned');
        error.value = 'Your account has no permissions. Please contact your administrator.';
        resetAuthenticatedState();
        return;
      }
      
      permissionsVerified.value = true;
      error.value = null;
      console.log('[AuthStore] User fully authenticated with permissions verified');
    } catch (err) {
      console.error('Failed to load permissions:', err);
      resetAuthenticatedState();
      error.value = err instanceof Error ? err.message : 'Failed to verify permissions';
    }
  }

  async function loginPopup() {
    try {
      isLoading.value = true;
      error.value = null;
      const response = await authService.loginPopup();
      account.value = response.account;
      
      // Load permissions after successful login
      await loadPermissions();
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Login failed';
      console.error('Login error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function loginRedirect() {
    try {
      error.value = null;
      await authService.loginRedirect();
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Login failed';
      console.error('Login error:', err);
      throw err;
    }
  }

  async function logout() {
    try {
      isLoading.value = true;
      error.value = null;
      await authService.logout();
      resetAuthenticatedState();
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Logout failed';
      console.error('Logout error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function getAccessToken(scopes?: string[]): Promise<string> {
    try {
      error.value = null;
      return await authService.getAccessToken(scopes);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to get access token';
      console.error('Token acquisition error:', err);
      throw err;
    }
  }

  function clearError() {
    error.value = null;
  }

  return {
    // State
    account,
    isInitialized,
    isLoading,
    error,
    isAuthenticated,
    permissionsVerified,
    userInfo,

    // Permissions state (delegated to permissions composable)
    permissions: permissionsComposable.permissionsSummary,
    isLoadingPermissions: permissionsComposable.isLoading,
    userRoles,
    userScopes,
    hasPermissions,

    // Actions
    initialize,
    loginPopup,
    loginRedirect,
    logout,
    getAccessToken,
    loadPermissions,
    clearError,
  };
});
